import { format } from 'date-fns';
import moment from 'moment';

import { getDateFnsLocale } from '@rover/components/src/datetime/BookingsCalendar/utils';
import { SERVICE_TYPE_CHOICES } from '@rover/constants/src/translations/service.constants';
import Boarding from '@rover/icons/svg/boarding.svg';
import Daycare from '@rover/icons/svg/daycare.svg';
import DropIn from '@rover/icons/svg/drop-in.svg';
import Sitting from '@rover/icons/svg/sitting.svg';
import Walking from '@rover/icons/svg/walking.svg';
import { DateTimeFormatType } from '@rover/react-lib/src/context/I18nContext';
import dayFactory from '@rover/react-lib/src/factories/dayFactory';
import { DayEnum } from '@rover/rsdk/src/apiClient/latest/model/dayEnum';
import type { DateRangeField } from '@rover/types/src/datetime/DateRange';
import type { Day } from '@rover/types/src/datetime/Day';

export const getServiceTypeIcon = (
  service: string | undefined,
  color: string
): JSX.Element | null => {
  const iconStyle = { color, width: '24px', height: '24px' };

  const iconMap: Record<string, JSX.Element> = {
    [SERVICE_TYPE_CHOICES.boarding]: <Boarding style={iconStyle} />,
    [SERVICE_TYPE_CHOICES.traveling]: <Sitting style={iconStyle} />,
    [SERVICE_TYPE_CHOICES.dropIn]: <DropIn style={iconStyle} />,
    [SERVICE_TYPE_CHOICES.dayCare]: <Daycare style={iconStyle} />,
    [SERVICE_TYPE_CHOICES.walking]: <Walking style={iconStyle} />,
  };

  return service ? iconMap[service] || null : null;
};

export const makeDayFromDate = (date: Date, DF: DateTimeFormatType): Day => {
  return dayFactory({
    name: moment(date).format(DF.MONTH_DAY_MED),
    date,
  });
};

// Format date range for display
export const formatDateRange = (
  startDate: DateRangeField,
  endDate: DateRangeField,
  language: string
): string => {
  if (!startDate) return '';

  const locale = getDateFnsLocale(language);
  const dateFormat = 'MMM d';
  const yearDateFormat = 'MMM d, yyyy';

  const formatDate = (date: Date, includeYear: boolean = false): string => {
    const formatStr = includeYear ? yearDateFormat : dateFormat;
    return format(date, formatStr, { locale });
  };

  const start = startDate instanceof Date ? startDate : new Date(startDate);

  let end: Date | undefined;
  if (endDate) {
    end = endDate instanceof Date ? endDate : new Date(endDate);
  }

  const showYear =
    (end && start.getFullYear() !== end.getFullYear()) ||
    start.getFullYear() !== new Date().getFullYear();

  // If no end date or same day, show single date
  if (!end || start.getTime() === end.getTime()) {
    return formatDate(start, showYear);
  }

  // If different months, show both months
  if (start.getMonth() !== end.getMonth()) {
    return `${formatDate(start, showYear)} - ${formatDate(end, showYear)}`;
  }

  // Same month, show range
  const startFormatted = formatDate(start, false);
  const endFormatted = showYear ? `${end.getDate()} ${end.getFullYear()}` : `${end.getDate()}`;
  return `${startFormatted} - ${endFormatted}`;
};

export const formatSelectedDays = (selectedDays: Day[]): Record<DayEnum, boolean> => {
  const days = {
    monday: false,
    tuesday: false,
    wednesday: false,
    thursday: false,
    friday: false,
    saturday: false,
    sunday: false,
  };
  selectedDays.forEach(({ name }) => {
    days[name.toLowerCase()] = true;
  });
  return days;
};
